/**
 * Matching a pathname against a map keyed by path.
 *
 * Two config files now route by path — `whatsappContacts.ts` picks the seat
 * that answers for a page, `pageTips.ts` picks the tip it shows — and both
 * want the same rule: a key covers a page and everything under it, the longest
 * key wins, and nothing matches across a segment boundary. That rule is four
 * lines and three of them are easy to get subtly wrong, so it lives here once
 * rather than in each caller.
 *
 * It was `whatsappContact.ts`'s private helper until 2026-10-24, and the
 * behaviour is unchanged: the second caller is the reason it moved, not a
 * reason it does anything differently.
 */

/** Trailing slashes come off, so `/accessories/` matches the `/accessories` key. */
export function normalisePathname(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * True when `key` covers `path` — the page itself, or anything beneath it.
 *
 * The segment boundary is the whole point. A bare `startsWith` would let
 * `/cars` claim a future `/cars-we-have-sold`, which is the kind of bug that
 * surfaces months later as "why is the parts desk answering the archive page".
 *
 * `/` is the exception, and it is exact. As a prefix it would cover every path
 * on the site, so a map that wants to name the home page can — and a map that
 * does not want a catch-all is not given one by accident. It is why
 * `whatsappRouting` could never list `/` and `pageTipRouting` can.
 */
function covers(key: string, path: string): boolean {
  if (path === key) return true;
  return key !== '/' && path.startsWith(`${key}/`);
}

/**
 * The value whose key covers this path, or null when none does.
 *
 * Longest key wins, which is what makes the result independent of the order
 * the map happens to be typed out in: `/services/tax-financing` beats
 * `/services` wherever either line sits in the file.
 */
export function matchLongestPathEntry<T>(
  pathname: string,
  map: Readonly<Record<string, T>>,
): { key: string; value: T } | null {
  const path = normalisePathname(pathname);
  let matched: { key: string; value: T } | null = null;

  for (const [key, value] of Object.entries(map)) {
    if (!covers(key, path) || key.length <= (matched?.key.length ?? 0)) continue;
    matched = { key, value };
  }

  return matched;
}

/**
 * The matched value alone, for a caller that does not care which key found it.
 *
 * The key matters to the scroll tips — it is what a dismissal is remembered
 * against, so that closing a tip on `/cars` closes it for the car pages and
 * nowhere else — and does not matter at all to the WhatsApp routing, which
 * only wants the seat.
 */
export function matchLongestPathKey<T>(
  pathname: string,
  map: Readonly<Record<string, T>>,
): T | null {
  return matchLongestPathEntry(pathname, map)?.value ?? null;
}

/** True when any of `prefixes` covers this path, by the same rule as above. */
export function pathHasPrefix(pathname: string, prefixes: readonly string[]): boolean {
  const path = normalisePathname(pathname);
  return prefixes.some((prefix) => covers(prefix, path));
}
